import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Car, AlertCircle, ShieldAlert, Trophy, Gauge, Clock } from "lucide-react";
import { DateRangePicker } from "@/components/date-range-picker";
import { Button } from "@/components/ui/button";
import { useDateRange, defaultRange } from "@/lib/use-date-range";

interface LeagueRow {
  engineer: string;
  days_active: number;
  total_pings: number;
  total_miles: number;
  in_hours_miles: number;
  ooh_miles: number;
  max_daily_miles: number;
  avg_daily_miles: number;
  legs_dropped_jitter: number;
  legs_dropped_implausible: number;
}

interface VehicleUsageResponse {
  rangeStart: string;
  rangeEnd: string;
  method: string;
  parameters: {
    workingHoursLocal: string;
    minLegMiles: number;
    maxSpeedMph: number;
    timezone: string;
  };
  fleet: {
    engineers_with_data: number;
    fleet_total_miles: number;
    fleet_ooh_miles: number;
  };
  league: LeagueRow[];
  dataNotes: string[];
}

function fmtMiles(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })} mi`;
}
function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return n.toLocaleString("en-GB");
}

export default function VehicleUsage() {
  const { dateRange, setDateRange, from, to, rangeQs } = useDateRange();
  const { data, isLoading, error } = useQuery<VehicleUsageResponse>({
    queryKey: ["vehicle-usage", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/vehicle-usage${rangeQs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading vehicle usage…</div>;
  if (error || !data) return <div className="p-6 text-red-600">Failed: {error instanceof Error ? error.message : "unknown"}</div>;

  // Heat colour for OOH miles — more OOH = redder.
  const maxOoh = Math.max(1, ...data.league.map((r) => r.ooh_miles));
  const oohColor = (n: number): string => {
    const t = n / maxOoh;
    if (t < 0.25) return "";
    if (t < 0.6) return "bg-amber-50 dark:bg-amber-950/30";
    return "bg-rose-50 dark:bg-rose-950/30";
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Car className="h-6 w-6" /> Vehicle Usage
          </h1>
          <p className="text-sm text-muted-foreground">
            Per-engineer mileage from Commusoft diary check-ins. {from && to ? `${from} → ${to}` : "Last 60 days."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="ghost" size="sm" onClick={() => setDateRange(defaultRange())}>Reset</Button>
        </div>
      </div>

      {/* Headline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={<Trophy className="h-3.5 w-3.5" />} label="Engineers with data" value={fmtInt(data.fleet.engineers_with_data)} />
        <KpiCard icon={<Gauge className="h-3.5 w-3.5" />} label="Fleet total miles" value={fmtMiles(data.fleet.fleet_total_miles)} />
        <KpiCard icon={<Clock className="h-3.5 w-3.5" />} label="Fleet OOH miles" value={fmtMiles(data.fleet.fleet_ooh_miles)} tone={data.fleet.fleet_total_miles > 0 && data.fleet.fleet_ooh_miles / data.fleet.fleet_total_miles > 0.2 ? "warn" : undefined} />
        <KpiCard icon={<Car className="h-3.5 w-3.5" />} label="OOH % of fleet" value={data.fleet.fleet_total_miles > 0 ? `${((data.fleet.fleet_ooh_miles / data.fleet.fleet_total_miles) * 100).toFixed(1)}%` : "—"} />
      </div>

      {/* Engineer league */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Engineer league</CardTitle>
          <p className="text-xs text-muted-foreground">
            Ranked by total miles. OOH column heat-coloured (amber → red as the OOH share grows).
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-1">Rank</th>
                <th>Engineer</th>
                <th className="text-right">Days active</th>
                <th className="text-right">Pings</th>
                <th className="text-right">Total miles</th>
                <th className="text-right">In-hours</th>
                <th className="text-right">OOH</th>
                <th className="text-right">OOH %</th>
                <th className="text-right">Avg / day</th>
                <th className="text-right">Max day</th>
              </tr>
            </thead>
            <tbody>
              {data.league.map((r, i) => {
                const oohPct = r.total_miles > 0 ? (r.ooh_miles / r.total_miles) * 100 : 0;
                return (
                  <tr key={r.engineer} className="border-t">
                    <td className="py-1.5 pr-2 font-mono">{i + 1}</td>
                    <td className="font-medium">{r.engineer}</td>
                    <td className="text-right font-mono">{fmtInt(r.days_active)}</td>
                    <td className="text-right font-mono text-muted-foreground">{fmtInt(r.total_pings)}</td>
                    <td className="text-right font-mono font-semibold">{fmtMiles(r.total_miles)}</td>
                    <td className="text-right font-mono">{fmtMiles(r.in_hours_miles)}</td>
                    <td className={`text-right font-mono ${oohColor(r.ooh_miles)}`}>{fmtMiles(r.ooh_miles)}</td>
                    <td className="text-right font-mono">{oohPct.toFixed(1)}%</td>
                    <td className="text-right font-mono">{fmtMiles(r.avg_daily_miles)}</td>
                    <td className="text-right font-mono text-muted-foreground">{fmtMiles(r.max_daily_miles)}</td>
                  </tr>
                );
              })}
              {data.league.length === 0 && (
                <tr><td colSpan={10} className="py-3 text-muted-foreground text-center">No GPS data for the selected window.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Methodology + caveats */}
      <Card className="border-amber-500/40">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" /> Method & caveats
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs text-muted-foreground">
          <p><b>Source:</b> Commusoft <code className="font-mono">diarytimetrackings</code> table — engineer status-change check-ins on the mobile app (arrived, in-progress, complete) — joined to <code className="font-mono">diaries.engineerid</code> for who the trip was for.</p>
          <p><b>Mileage method:</b> straight-line (Haversine) distance between consecutive check-in locations per engineer per day. Pings within {data.parameters.minLegMiles} mi of each other (GPS jitter) are dropped. Legs implying &gt; {data.parameters.maxSpeedMph} mph are discarded as data errors.</p>
          <p><b>Working hours:</b> {data.parameters.workingHoursLocal}. Pings outside are classified OOH.</p>
          <p className="text-amber-700 dark:text-amber-300"><b>Known limits:</b> straight-line distance underestimates actual driven miles (no detour for traffic / road network). Engineers who don't use mobile-app status check-ins won't appear. OOH detection doesn't yet distinguish "diarised call-out" from "personal use" — that needs the next iteration to flag OOH trips against the diary event's typeofevent.</p>
          <ul className="list-disc list-inside">
            {data.dataNotes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </CardContent>
      </Card>

      <Card className="border-muted">
        <CardContent className="p-4 text-xs text-muted-foreground flex gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-500 flex-shrink-0" />
          <div>
            GPS data tied to a named driver is personal data under UK GDPR. This report is admin-only.
            Required next steps before showing engineers themselves: staff informed, retention limit set, DPIA for systematic monitoring,
            and an accountant flag for any van/fuel benefit-in-kind (P11D) implications if used for personal-mileage discussions.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  icon, label, value, tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "warn" | "bad";
}) {
  const ring = tone === "warn" ? "border-amber-500/40" : tone === "bad" ? "border-red-500/40" : "";
  const text = tone === "warn" ? "text-amber-600" : tone === "bad" ? "text-red-600" : "";
  return (
    <Card className={ring}>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-xs flex items-center gap-1.5">{icon}{label}</div>
        <div className={`text-xl font-bold ${text}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
